<template>
  <figure ref="root" class="mono-coast-media" :class="{'is-playing':playing}">
    <div class="mono-coast-image">
      <video ref="video" :src="near?'/assets/vid/home5-inline.mp4':undefined" poster="/assets/img/home5-one-second.webp" :muted="muted" :controls="nativeControls" playsinline webkit-playsinline preload="auto" aria-label="Waves at Warrnambool" @loadedmetadata="prime" @seeking="onSeeking" @seeked="onSeeked" @loadeddata="frameReady" @canplay="frameReady" @playing="onPlaying" @pause="onPause" @ended="beginLoop" @error="blockedPlayback"/>
      <img src="/assets/img/home5-one-second.webp" width="607" height="1080" alt="Cormorants beside the sea at Warrnambool" :style="{opacity:videoReady||nativeControls?0:1}"/>
      <canvas ref="canvas" aria-hidden="true" :style="{opacity:showDrawing?1:0}" @webglcontextlost.prevent="failed=true"/>
      <button v-if="showPlay" type="button" class="mono-coast-play" aria-label="Play the coast video" @touchstart.passive="startPlayTouch" @touchmove.passive="movePlayTouch" @touchcancel="playTouch=null" @touchend="endPlayTouch" @click="clickPlay">
        <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 11 7-11 7z"/></svg></span>
      </button>
    </div>
    <figcaption>Warrnambool, Australia · June 2025</figcaption>
    <button class="mono-coast-sound" :aria-label="muted?'Listen to the coast':'Mute the coast'" :aria-pressed="!muted" @click="sound"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h4l5-4v12l-5-4H4z"/><path v-if="muted" d="m17 9 5 6m0-6-5 6"/><path v-else d="M17 8q5 4 0 8m3-11q8 7 0 14"/></svg>{{muted?'Listen to the coast':'Sound on'}}</button>
  </figure>
</template>
<script>
import{WebGLRenderer,Scene,OrthographicCamera,PlaneGeometry,ShaderMaterial,Mesh,TextureLoader,VideoTexture,Vector2}from'three';
import { COAST_LOOP, coastLoopAppearance } from './coastLoop';
const fragment=`
uniform sampler2D picture;
uniform vec2 texel;
uniform vec2 crop;
uniform float progress;
uniform float night;
uniform vec2 development;
varying vec2 uv0;
float coastLuma(vec3 c){return dot(c,vec3(.299,.587,.114));}
float l(vec2 off){vec2 p=(uv0-.5)*crop+.5+off*texel;vec3 c=texture2D(picture,p).rgb*4.;c+=texture2D(picture,p+texel*vec2(3.,3.)).rgb;c+=texture2D(picture,p+texel*vec2(-3.,3.)).rgb;c+=texture2D(picture,p+texel*vec2(3.,-3.)).rgb;c+=texture2D(picture,p-texel*vec2(3.,3.)).rgb;return coastLuma(c/8.);}
void main(){
 vec3 colour=texture2D(picture,(uv0-.5)*crop+.5).rgb;
 float stepSize=mix(4.,2.,smoothstep(.1,.38,progress));
 float gx=l(vec2(stepSize,0.))-l(vec2(-stepSize,0.));
 float gy=l(vec2(0.,stepSize))-l(vec2(0.,-stepSize));
 float edge=length(vec2(gx,gy));
 float line=smoothstep(.15,.36,edge);
 float scan=smoothstep(0.,.075,progress*3.6-(1.-uv0.y)*.63-uv0.x*.13);
 // Match the site dark background (#1c1c1e) while the image is drawn or blank.
 vec3 paper=mix(vec3(1.),vec3(28.,28.,30.)/255.,night);
 vec3 ink=mix(vec3(.06),vec3(.92),night);
 float lineVisibility=min(scan,development.x);
 vec3 drawing=mix(paper,ink,line*lineVisibility*.6);
 float resolve=min(smoothstep(.25,.76,progress),development.y);
 gl_FragColor=vec4(mix(drawing,colour,resolve),1.);
}`;
// The native video presents moving frames. WebGL only draws the transitions,
// sampling that same video so scroll exit preserves the current frame.
export default {
  props: { progress: Number, reduced: Boolean, dark: Boolean },
  data: () => ({ ready: false, videoReady: false, failed: false, near: false, visible: false, playing: false, playBlocked: false, muted: true, manualPlayback: false, nativeControls: false, loopStage: 'idle', loopAppearance: coastLoopAppearance('idle', 0) }),
  computed: {
    showDrawing() {
      return this.ready && !this.reduced && !this.failed && !(this.nativeControls && this.progress > .8) && (!this.videoReady || this.progress < .8 || this.loopStage !== 'idle');
    },
    showPlay() {
      return !this.nativeControls && this.visible && (this.reduced || this.progress > .8) && this.loopStage === 'idle' && !this.playing && (this.playBlocked || (this.reduced && !this.manualPlayback));
    },
  },
  mounted() {
    this.io = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible && !this.near) {
        this.near = true;
        this.setup();
        this.$nextTick(this.playback);
      }
      this.playback();
    }, { rootMargin: '100px' });
    this.io.observe(this.$refs.root);
    this.visibility = () => this.playback();
    document.addEventListener('visibilitychange', this.visibility);
  },
  watch: {
    progress() { this.playback(); this.render(); },
    dark() { this.render(); },
    reduced() {
      if (this.reduced && this.loopStage !== 'idle') {
        this.stopLoop();
        this.loopStage = 'idle';
        this.loopAppearance = coastLoopAppearance('idle', 0);
        if (this.$refs.video?.ended) this.restart();
      }
      if (!this.reduced && this.near && !this.renderer && !this.failed) this.setup();
      this.playback();
    },
  },
  beforeUnmount() {
    this.destroyed = true;
    this.io?.disconnect();
    this.$refs.video?.pause();
    this.stopPlaybackWatch();
    this.stopSeekWatch();
    this.stopLoop();
    document.removeEventListener('visibilitychange', this.visibility);
    this.posterTexture?.dispose();
    this.videoTexture?.dispose();
    this.geometry?.dispose();
    this.material?.dispose();
    this.renderer?.dispose();
  },
  methods: {
    setup() {
      if (this.reduced) return;
      try {
        this.renderer = new WebGLRenderer({ canvas: this.$refs.canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
        this.renderer.setSize(607, 1080, false);
        this.scene = new Scene();
        this.camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
        this.geometry = new PlaneGeometry(2, 2);
        this.material = new ShaderMaterial({
          uniforms: {
            picture: { value: null }, texel: { value: new Vector2(1 / 607, 1 / 1080) },
            crop: { value: new Vector2(1, 1) }, progress: { value: this.progress }, night: { value: this.dark ? 1 : 0 }, development: { value: new Vector2(1, 1) },
          },
          vertexShader: 'varying vec2 uv0;void main(){uv0=uv;gl_Position=vec4(position,1.);}',
          fragmentShader: fragment,
        });
        this.scene.add(new Mesh(this.geometry, this.material));
        new TextureLoader().load('/assets/img/home5-one-second.webp', texture => {
          if (this.destroyed) { texture.dispose(); return; }
          this.posterTexture = texture;
          if (!this.videoReady) this.material.uniforms.picture.value = texture;
          this.ready = true;
          this.render();
        }, undefined, () => { this.failed = true; });
        if (this.videoReady) this.useVideo();
      } catch {
        this.ready = false;
        this.failed = true;
      }
    },
    prime() {
      const video = this.$refs.video;
      this.startTime = Math.min(1, Math.max(0, video.duration - .05));
      this.primed = true;
      video.currentTime = this.startTime;
      this.playback();
    },
    onSeeking() {
      this.playing = false;
      this.playSamples = 0;
      this.lastMediaTime = this.$refs.video.currentTime;
    },
    onSeeked() {
      // A seek changes currentTime without playing. Start measuring after it.
      this.lastMediaTime = this.$refs.video.currentTime;
      this.lastAdvanceAt = performance.now();
      this.frameReady();
    },
    frameReady() {
      const video = this.$refs.video;
      if (this.destroyed || !this.primed || video.seeking || video.readyState < 2 || !video.videoWidth || !video.videoHeight || video.currentTime < this.startTime - .01) return;
      this.videoReady = true;
      if (this.loopStage === 'seeking') {
        this.stopSeekWatch();
        this.loopStage = 'in';
        this.loopElapsed = 0;
      }
      this.useVideo();
      this.render();
      this.playback();
    },
    useVideo() {
      if (!this.material || this.destroyed) return;
      const video = this.$refs.video;
      if (!this.videoTexture) this.videoTexture = new VideoTexture(video);
      const sourceAspect = video.videoWidth / video.videoHeight;
      const targetAspect = 607 / 1080;
      this.material.uniforms.picture.value = this.videoTexture;
      this.material.uniforms.texel.value.set(1 / video.videoWidth, 1 / video.videoHeight);
      this.material.uniforms.crop.value.set(Math.min(1, targetAspect / sourceAspect), Math.min(1, sourceAspect / targetAspect));
      this.ready = true;
    },
    render() {
      if (!this.material?.uniforms.picture.value || !this.renderer || this.destroyed || this.reduced || this.failed) return;
      if (this.videoReady) this.videoTexture.needsUpdate = true;
      this.material.uniforms.progress.value = this.progress;
      this.material.uniforms.night.value = this.dark ? 1 : 0;
      const { lines, image } = this.loopAppearance;
      this.material.uniforms.development.value.set(lines, image);
      try {
        this.renderer.render(this.scene, this.camera);
      } catch {
        // A failed texture upload must leave the native video usable.
        this.failed = true;
      }
    },
    shouldPlay() {
      return this.visible && !document.hidden && (this.reduced ? this.manualPlayback || !this.muted : this.progress > .8);
    },
    startPlayTouch(event) {
      const touch = event.touches.length === 1 ? event.touches[0] : null;
      this.playTouch = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    movePlayTouch(event) {
      const touch = event.touches[0];
      if (!touch || event.touches.length !== 1 || (this.playTouch && Math.hypot(touch.clientX - this.playTouch.x, touch.clientY - this.playTouch.y) > 10)) this.playTouch = null;
    },
    endPlayTouch(event) {
      const touch = event.changedTouches[0];
      const tapped = this.playTouch && touch && Math.hypot(touch.clientX - this.playTouch.x, touch.clientY - this.playTouch.y) <= 10;
      this.playTouch = null;
      if (!tapped) return;
      event.preventDefault();
      // Start inside WebKit's trusted touchend. Do not wait for a synthetic click.
      this.lastPlayTouch = performance.now();
      this.requestPlayback(true);
    },
    clickPlay(event) {
      if (event.detail !== 0 && this.lastPlayTouch != null && performance.now() - this.lastPlayTouch < 800) return;
      this.requestPlayback(true);
    },
    blockedPlayback() {
      this.playBlocked = true;
      if (this.manualPlayback) this.nativeControls = true;
    },
    requestPlayback(fromGesture = false) {
      const video = this.$refs.video;
      if (!video || this.destroyed || (!fromGesture && (this.playPending || this.playBlocked))) return;
      if (fromGesture) {
        this.manualPlayback = true;
        // Cancel a stuck request in this same user gesture. Keep the button
        // present until two advancing samples confirm actual playback.
        if (this.playBlocked && !video.paused) video.pause();
      }
      const attempt = this.playAttempt = (this.playAttempt || 0) + 1;
      video.defaultMuted = this.muted;
      video.muted = this.muted;
      video.playsInline = true;
      if (fromGesture && video.error) video.load();
      this.playPending = true;
      this.startPlaybackWatch();
      // Call play synchronously inside a click handler even when no frame has
      // decoded yet. Some embedded browsers defer decoding until this call.
      video.play().catch(error => {
        if (!this.destroyed && attempt === this.playAttempt && error.name !== 'AbortError') this.blockedPlayback();
      }).finally(() => {
        if (attempt === this.playAttempt) this.playPending = false;
      });
    },
    startPlaybackWatch() {
      this.stopPlaybackWatch();
      this.playSamples = 0;
      this.lastMediaTime = this.$refs.video.currentTime;
      this.lastAdvanceAt = performance.now();
      this.playbackWatch = setInterval(this.checkPlayback, 250);
    },
    checkPlayback() {
      if (this.destroyed || !this.shouldPlay() || this.loopStage !== 'idle') {
        this.stopPlaybackWatch();
        return;
      }
      const video = this.$refs.video;
      const now = performance.now();
      if (!video.seeking && !video.paused && video.readyState >= 2 && video.currentTime > this.lastMediaTime + .015) {
        this.lastAdvanceAt = now;
        this.playSamples++;
        if (this.playSamples >= 2) {
          this.playing = true;
          this.playBlocked = false;
          this.nativeControls = false;
          if (!this.videoReady) this.frameReady();
        }
      } else if (now - this.lastAdvanceAt >= 2500) {
        this.playing = false;
        this.playSamples = 0;
        this.blockedPlayback();
      }
      this.lastMediaTime = video.currentTime;
    },
    stopPlaybackWatch() {
      if (this.playbackWatch != null) clearInterval(this.playbackWatch);
      this.playbackWatch = null;
    },
    playback() {
      const video = this.$refs.video;
      if (!video || !this.near || !video.getAttribute('src') || this.destroyed) return;
      const run = this.shouldPlay();
      if (this.loopStage !== 'idle') {
        this.stopPlaybackWatch();
        video.pause();
        if (!run) {
          this.stopLoop();
          this.stopSeekWatch();
        } else if (this.loopStage === 'seeking') this.watchLoopSeek();
        else if (this.loopFrame == null) this.loopFrame = requestAnimationFrame(this.advanceLoop);
        return;
      }
      if (run) {
        if (video.paused) this.requestPlayback();
      } else {
        this.stopPlaybackWatch();
        if (this.playPending) {
          this.playAttempt++;
          this.playPending = false;
        }
        video.pause();
      }
    },
    onPlaying() {
      if (this.$refs.video.paused) return;
      this.frameReady();
      this.playback();
      this.render();
      if (this.shouldPlay() && this.loopStage === 'idle' && this.playbackWatch == null) this.startPlaybackWatch();
    },
    onPause() {
      const video = this.$refs.video;
      if (!video.paused) return;
      this.playing = false;
      if (this.shouldPlay() && this.loopStage === 'idle' && !video.ended) this.blockedPlayback();
      this.stopPlaybackWatch();
      this.render();
    },
    restart() {
      // This seek happens only while the shader is blank, never on scroll exit.
      this.$refs.video.currentTime = this.startTime;
    },
    watchLoopSeek() {
      if (this.seekWatch != null) return;
      // Some embedded players will only decode after another play gesture.
      // Recover to the native frame and a usable control if seeking stalls.
      this.seekWatch = setTimeout(() => {
        this.seekWatch = null;
        if (this.destroyed || this.loopStage !== 'seeking' || !this.shouldPlay()) return;
        this.loopStage = 'idle';
        this.loopAppearance = coastLoopAppearance('idle', 0);
        this.playing = false;
        this.blockedPlayback();
        this.render();
      }, 2500);
    },
    stopSeekWatch() {
      if (this.seekWatch != null) clearTimeout(this.seekWatch);
      this.seekWatch = null;
    },
    beginLoop() {
      this.playing = false;
      this.stopPlaybackWatch();
      if (this.reduced || this.failed) { this.restart(); return; }
      this.loopStage = 'out';
      this.loopAppearance = coastLoopAppearance('out', 0);
      this.loopElapsed = 0;
      this.loopLast = 0;
      this.playback();
    },
    advanceLoop(now) {
      this.loopFrame = null;
      if (this.destroyed) return;
      this.loopElapsed += this.loopLast ? Math.min(64, now - this.loopLast) : 0;
      this.loopLast = now;
      if (this.loopStage === 'out') {
        this.loopAppearance = coastLoopAppearance('out', this.loopElapsed);
        if (this.loopElapsed >= COAST_LOOP.out) {
          this.loopStage = 'seeking';
          this.loopLast = 0;
          // The drawing is already invisible. Decode the opening immediately
          // without adding a separate, visibly stationary white hold.
          this.restart();
        }
      } else if (this.loopStage === 'in') {
        this.loopAppearance = coastLoopAppearance('in', this.loopElapsed);
        if (this.loopElapsed >= COAST_LOOP.in) this.loopStage = 'idle';
      }
      this.render();
      this.playback();
    },
    stopLoop() {
      if (this.loopFrame != null) cancelAnimationFrame(this.loopFrame);
      this.loopFrame = null;
      this.loopLast = 0;
    },
    sound() {
      this.muted = !this.muted;
      this.$refs.video.muted = this.muted;
      if (this.loopStage === 'idle' && this.shouldPlay()) this.requestPlayback(true);
      else this.playback();
    },
  },
};
</script>
